/**
 * Embeddable market appraisal HTML preview only.
 * Used inside the preview modal iframe — no layout, menu, or actions.
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import api from '../services/api';
import { StorageManager } from '../utils/storageManager';

const ConsultationReportPreviewEmbed: React.FC = () => {
  const { agencySlug, propertyId } = useParams<{ agencySlug: string; propertyId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [htmlContent, setHtmlContent] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      const storedInputs = propertyId
        ? sessionStorage.getItem(`appraisal_preview_${propertyId}`)
        : null;

      let inputsFromUI: any = null;
      if (storedInputs) {
        try {
          inputsFromUI = JSON.parse(storedInputs);
          sessionStorage.removeItem(`appraisal_preview_${propertyId}`);
        } catch (err) {
          console.error('Error parsing stored inputs:', err);
        }
      }

      if (inputsFromUI) {
        fetchPreview(inputsFromUI);
      } else {
        fetchPreview();
      }
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencySlug, propertyId]);

  const fetchPreview = async (inputsToUse?: any) => {
    if (!agencySlug || !propertyId) {
      setError('Missing agency or property ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = StorageManager.getToken();
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      const isPost = !!inputsToUse;

      if (isPost) {
        const response = await api.post(
          `/agencies/${agencySlug}/properties/${propertyId}/consultation-report/preview`,
          { structuredInputs: inputsToUse },
          {
            responseType: 'text',
            headers: {
              Accept: 'text/html',
              'Content-Type': 'application/json',
            },
          }
        );
        setHtmlContent(response.data);
      } else {
        const response = await api.get(
          `/agencies/${agencySlug}/properties/${propertyId}/consultation-report/preview`,
          {
            responseType: 'text',
            headers: { Accept: 'text/html' },
          }
        );
        setHtmlContent(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching preview:', err);
      setError(
        err.response?.data?.error ||
          err.message ||
          'Failed to load preview.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!htmlContent) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Typography color="text.secondary">No preview content available.</Typography>
      </Box>
    );
  }

  return (
    <iframe
      srcDoc={htmlContent}
      style={{
        width: '100%',
        height: '100vh',
        border: 'none',
        overflow: 'auto',
        display: 'block',
      }}
      title="Market Appraisal Preview"
    />
  );
};

export default ConsultationReportPreviewEmbed;
